// #3229 Story B — the API refuses a token whose workspace claim no longer has a
// live membership with 401 { error, code: 'TENANT_ACCESS_REVOKED' } (founder
// ruling D6: force re-authentication; login re-mints the claim from live
// membership).
//
// Handled once, in apiFetch:
//  - Bearer refusal: clear every stored credential (the same keys
//    AuthContext.logout clears, partner context included), only if the refused
//    request's token is still the stored one (another tab's newer login must
//    survive, #3249); then a FULL navigation to /login — or, when already on
//    /login, tell AuthProvider to drop its in-memory session (#3269).
//  - Cookie-only refusal (magic-link action session, no Bearer sent): there is
//    nothing stored to clear (the API clears the HttpOnly cookie on the 401,
//    #3268). If another tab has since stored a Bearer login, that login wins
//    and nothing happens; otherwise redirect to /login once.
// Parallel refusals don't each redirect (module-level guard), and there is no
// loop: nothing authenticated runs without a token, and /login makes no API
// call on mount.

export const TENANT_ACCESS_REVOKED = 'TENANT_ACCESS_REVOKED';
export const TENANT_ACCESS_REVOKED_LOGIN_ERROR = 'tenant_access_revoked';
export const TENANT_ACCESS_REVOKED_MESSAGE = 'Your access to this workspace has been removed. Please sign in again.';
/** Dispatched on `window` (detail: { refusedToken }) when stored credentials were cleared on /login; AuthProvider listens (#3269). */
export const TENANT_ACCESS_REVOKED_EVENT = 'tenant-access-revoked';

const STORED_CREDENTIAL_KEYS = [
  'blueprint_token',
  'blueprint_user',
  'partnerContextToken',
  'partnerOriginalToken',
  'partnerReturnPath',
  'partnerAccountId',
] as const;

let redirecting = false;

/** Test-only: reset the once-per-page guard. */
export function __resetTenantAccessRevokedGuardForTest(): void {
  redirecting = false;
}

export function isTenantAccessRevoked(status: number, body: unknown): boolean {
  return status === 401
    && typeof body === 'object' && body !== null
    && (body as Record<string, unknown>).code === TENANT_ACCESS_REVOKED;
}

/**
 * Called with the Bearer token the REFUSED request actually sent (null when it
 * sent none, i.e. it was authenticated by the action-session cookie). In order:
 *   1. once-only guard: a redirect is already under way → do nothing;
 *   2. cookie-only refusal (#3268): if a Bearer token is NOW stored (another
 *      tab signed in), that login wins → do nothing; otherwise there is nothing
 *      to clear → redirect once;
 *   3. Bearer refusal: only if that token is still the stored one (#3249) are
 *      credentials cleared; then redirect once — or, already on /login (no
 *      loop), tell AuthProvider to drop its in-memory session (#3269).
 * Returns true when it started the redirect.
 */
export function handleTenantAccessRevoked(
  refusedToken: string | null,
  win: Pick<Window, 'location' | 'localStorage' | 'dispatchEvent'> = window,
): boolean {
  if (redirecting) return false;
  const stored = win.localStorage.getItem('blueprint_token');
  if (refusedToken === null) {
    if (stored !== null) return false;
    return redirectToLoginOnce(win);
  }
  if (stored !== refusedToken) return false;
  for (const key of STORED_CREDENTIAL_KEYS) win.localStorage.removeItem(key);
  if (redirectToLoginOnce(win)) return true;
  // Not redirecting (already on /login): no full navigation will rebuild
  // AuthProvider from storage, so tell it to drop its in-memory session now.
  // Only here — dispatching before a redirect would race the navigation with
  // SPA route guards reacting to the reset (R1 LOW-4).
  // Carries the refused token: AuthProvider resets only if it still holds it
  // (PR #3275 R2 — never a newer session).
  win.dispatchEvent(new CustomEvent(TENANT_ACCESS_REVOKED_EVENT, { detail: { refusedToken } }));
  return false;
}

function redirectToLoginOnce(win: Pick<Window, 'location'>): boolean {
  if (win.location.pathname === '/login') return false;
  redirecting = true;
  win.location.assign(`/login?error=${TENANT_ACCESS_REVOKED_LOGIN_ERROR}`);
  return true;
}
